import React, { useState } from "react";
import { ArrowRight, Loader2, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { IMAGES } from "../data/content";

const INTERESTS = [
  "Talking Trails Development",
  "Interpretive Signage",
  "Oral History Recording",
  "Cultural Interpretation",
  "Heritage Storytelling",
  "Tourism Experience Design",
  "Discovery Call",
  "Other",
];

const initial = { name: "", email: "", organisation: "", interest: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      setForm(initial);
      toast.success("Thank you — we will be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 bg-brand-ink text-brand-cream overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${IMAGES.coastalPath})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/60 to-brand-ink/95" />
        <div className="container-px relative z-10">
          <div className="overline text-brand-ochre">Contact</div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.04] max-w-4xl">
            Start a <span className="italic font-light">conversation.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-brand-cream/80 text-lg font-light leading-relaxed">
            Tell us about the place, the people and the experience you would like to
            create. We will be in touch within a few days.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <div className="overline">Get in touch</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-brand-charcoal leading-tight">
              We listen first.
            </h2>
            <p className="mt-5 text-brand-muted font-light">
              Every Talking Trails begins with a conversation. Whether you are scoping a
              new visitor experience or simply curious, we would love to hear from you.
            </p>

            <ul className="mt-10 space-y-5 text-brand-charcoal">
              <li className="flex items-start gap-4">
                <Mail size={16} className="text-brand-ochre mt-1" />
                <div>
                  <div className="overline">Email</div>
                  <a href="mailto:william@talkingtrails.com" className="block mt-1 hover:text-brand-ochre transition-colors">
                    william@talkingtrails.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin size={16} className="text-brand-ochre mt-1" />
                <div>
                  <div className="overline">Based on</div>
                  <span className="block mt-1">Country, across Australia</span>
                </div>
              </li>
            </ul>

            <div className="mt-12 p-6 border-l-2 border-brand-ochre bg-brand-sand/60">
              <p className="font-serif text-xl md:text-2xl text-brand-charcoal italic leading-snug">
                &ldquo;Every place has a story. We just help it be heard.&rdquo;
              </p>
            </div>
          </div>

          <div className="lg:col-span-8">
            {done ? (
              <SuccessCard onReset={() => setDone(false)} />
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-brand-cream border border-brand-line/70 p-8 md:p-12"
              >
                <div className="overline">Tell us about your project</div>
                <h3 className="font-serif text-3xl mt-3 text-brand-charcoal">A few details to get us started.</h3>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your name *">
                    <input
                      required
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={onChange("name")}
                      className="form-input"
                      placeholder="e.g. Alex Park"
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      required
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={onChange("email")}
                      className="form-input"
                      placeholder="you@email.com"
                    />
                  </Field>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Organisation">
                    <input
                      data-testid="contact-input-org"
                      value={form.organisation}
                      onChange={onChange("organisation")}
                      className="form-input"
                      placeholder="Council, park, museum, community group"
                    />
                  </Field>
                  <Field label="Interested in">
                    <select
                      data-testid="contact-input-interest"
                      value={form.interest}
                      onChange={onChange("interest")}
                      className="form-input bg-transparent"
                    >
                      <option value="">Select an area</option>
                      {INTERESTS.map((i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label="Your message *" className="mt-6">
                  <textarea
                    required
                    rows={6}
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={onChange("message")}
                    className="form-input resize-none"
                    placeholder="Tell us about the place, the people and the experience you are imagining."
                  />
                </Field>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="mt-8 inline-flex items-center gap-2 bg-brand-rainforest text-brand-cream px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-brand-rainforestDark transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  {loading ? "Sending" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>

        <style>{`
          .form-input {
            width: 100%;
            background: transparent;
            border: none;
            border-bottom: 1px solid rgba(38,38,38,0.25);
            padding: 12px 0;
            font-family: 'Work Sans', sans-serif;
            font-size: 0.95rem;
            color: #262626;
            outline: none;
            transition: border-color 240ms ease;
          }
          .form-input::placeholder { color: rgba(38,38,38,0.4); }
          .form-input:focus { border-color: #C06E52; }
          select.form-input { appearance: none; padding-right: 1.5rem; }
        `}</style>
      </section>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block overline text-brand-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SuccessCard({ onReset }) {
  return (
    <div
      data-testid="contact-success"
      className="bg-brand-rainforest text-brand-cream p-10 md:p-14 relative overflow-hidden"
    >
      <div className="overline text-brand-cream/80">Sent</div>
      <h3 className="font-serif text-4xl mt-3 leading-tight">Thank you — we will be in touch.</h3>
      <p className="mt-5 text-brand-cream/80 font-light leading-relaxed max-w-md">
        Your message has reached us. We respond within a few days and always start with
        a conversation about place and people.
      </p>
      <button
        onClick={onReset}
        className="mt-8 inline-flex items-center gap-2 border border-brand-cream/80 text-brand-cream px-6 py-3 text-xs uppercase tracking-[0.22em] hover:bg-brand-cream hover:text-brand-rainforest transition-colors"
      >
        Send another
      </button>
    </div>
  );
}
